import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DownloadIcon from '@mui/icons-material/Download';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import FolderIcon from '@mui/icons-material/Folder';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LinkIcon from '@mui/icons-material/Link';
import LockIcon from '@mui/icons-material/Lock';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ShareIcon from '@mui/icons-material/Share';
import {
    Alert,
    Box,
    Button,
    ButtonBase,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    IconButton,
    LinearProgress,
    Paper,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';
import CopyButton from './CopyButton';
import { useTrackUsage } from '../../hooks/useTrackUsage';
import WorkFileService, { linkFolderService, workErrorCode, workErrorMessage } from '../../services/WorkFileService';
import { PageHeader } from '../common/PageHeader';
import { createPendingCompletions, UNDO_SECONDS } from '../tasks/pendingCompletion';
import { shortName } from '../tasks/taskUi';
import {
    ACTIVE,
    addedLine,
    bannerText,
    dayAgo,
    formatSize,
    languageStatus,
    progressLine,
    refusal,
    tabTitle,
    timeLeft,
} from './workFolderUi';
import { absoluteUrl, receivedBytes, sendPieces } from './workUpload';
import WorkShareDialog from './WorkShareDialog';

const GONE = 'This link is no longer available. Ask the person who shared it for a new link.';
const NAME_KEY = 'sharedFolderName';

// Codes that mean the file won't be added; the row says why and offers Dismiss
const NOT_ADDED = ['FOLDER_LOCKED', 'PROGRAM', 'SIZE_MISMATCH', 'NOT_ALLOWED', 'UPLOAD_NOT_OPEN'];

const folderKey = (languageKey) => languageKey || '';
let nextLocalId = 1;

/** A drop area: highlights while files are dragged over it, and hands the dropped files over. */
const useDrop = (enabled, onFiles) => {
    const [count, setCount] = useState(0);
    const depth = useRef(0);
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes('Files');
    const handlers = enabled
        ? {
              onDragEnter: (e) => {
                  if (!hasFiles(e)) return;
                  e.preventDefault();
                  depth.current += 1;
                  setCount(e.dataTransfer.items?.length || 1);
              },
              onDragOver: (e) => {
                  if (!hasFiles(e)) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'copy';
              },
              onDragLeave: () => {
                  depth.current -= 1;
                  if (depth.current <= 0) {
                      depth.current = 0;
                      setCount(0);
                  }
              },
              onDrop: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  depth.current = 0;
                  setCount(0);
                  const files = Array.from(e.dataTransfer.files || []);
                  if (files.length) onFiles(files);
              },
          }
        : {};
    return [count, handlers];
};

const headCell = { fontWeight: 500, fontSize: 12, color: 'text.secondary', py: 1, px: 1.5 };
const cell = { py: 1, px: 1.5, fontSize: 14 };

const UploadRow = ({ u, onCancel, onRetry, onRemove, onDismiss }) => {
    const pct = u.size ? Math.min(100, (u.sent / u.size) * 100) : 0;
    if (u.state === 'failed') {
        return (
            <TableRow sx={{ bgcolor: (t) => alpha(t.palette.error.main, 0.08) }}>
                <TableCell colSpan={3} sx={cell}>
                    <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                        <ErrorIcon color="error" sx={{ fontSize: 20 }} aria-hidden />
                        <Box>
                            <div>{u.title}</div>
                            <Typography variant="caption" color="text.secondary" component="div">
                                {u.caption}
                            </Typography>
                        </Box>
                    </Box>
                </TableCell>
                <TableCell align="right" sx={cell}>
                    <Button size="small" color="inherit" sx={{ color: 'text.secondary' }} onClick={onDismiss}>
                        Dismiss
                    </Button>
                </TableCell>
            </TableRow>
        );
    }
    const stopped = u.state === 'stopped';
    const waiting = u.state === 'waiting';
    const left =
        u.state === 'sending' && u.runStartedAt
            ? timeLeft(u.sent - u.runStartBytes, Date.now() - u.runStartedAt, u.size - u.sent)
            : null;
    return (
        <TableRow
            sx={{ bgcolor: (t) => (stopped ? alpha(t.palette.warning.main, 0.08) : alpha(t.palette.primary.main, 0.05)) }}
        >
            <TableCell sx={cell}>
                <div>{u.name}</div>
                <LinearProgress
                    variant="determinate"
                    value={waiting ? 0 : pct}
                    color={stopped ? 'warning' : 'primary'}
                    sx={{ mt: 0.75, width: 260, maxWidth: '100%', height: 4, borderRadius: 2 }}
                    aria-label={`${u.name} upload progress`}
                />
            </TableCell>
            <TableCell colSpan={2} sx={{ ...cell, color: 'text.secondary' }}>
                {waiting
                    ? 'Waiting · starts when the one above finishes'
                    : stopped
                      ? u.caption || `Stopped at ${Math.floor(pct)}%. Check your connection.`
                      : u.state === 'finishing'
                        ? `${Math.floor(pct)}% · Adding it to the folder`
                        : u.state === 'starting'
                          ? 'Starting'
                          : progressLine(u.sent, u.size, left)}
            </TableCell>
            <TableCell align="right" sx={{ ...cell, whiteSpace: 'nowrap' }}>
                {stopped ? (
                    <>
                        <Button size="small" onClick={onRetry}>
                            Retry
                        </Button>
                        <Button size="small" sx={{ color: 'text.secondary' }} onClick={onRemove}>
                            Remove
                        </Button>
                    </>
                ) : (
                    u.state !== 'finishing' && (
                        <Button size="small" onClick={onCancel}>
                            Cancel
                        </Button>
                    )
                )}
            </TableCell>
        </TableRow>
    );
};

/** Files and the uploads going to them. Newest first; Play and Download always, Delete if allowed. */
export const FileTable =({ files, uploads, canDelete, deleting, actions, dim }) => (
    <Box sx={{ overflowX: 'auto', px: 0.5, opacity: dim ? 0.5 : 1 }}>
        <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
            <TableHead>
                <TableRow>
                    <TableCell sx={headCell}>Name</TableCell>
                    <TableCell sx={{ ...headCell, width: 90 }}>Size</TableCell>
                    <TableCell sx={{ ...headCell, width: 280 }}>Added</TableCell>
                    <TableCell sx={{ ...headCell, width: 130 }} align="right">
                        <Box component="span" sx={visuallyHidden}>
                            Actions
                        </Box>
                    </TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {uploads.map((u) => (
                    <UploadRow
                        key={u.localId}
                        u={u}
                        onCancel={() => actions.cancel(u.localId)}
                        onRetry={() => actions.retry(u.localId)}
                        onRemove={() => actions.cancel(u.localId)}
                        onDismiss={() => actions.dismiss(u.localId)}
                    />
                ))}
                {files.map((f) => {
                    const [who, ...rest] = addedLine(f);
                    const gone = deleting[f.id];
                    return (
                        <TableRow key={f.id} sx={gone ? { opacity: 0.45, '& td': { textDecoration: 'line-through' } } : undefined}>
                            <TableCell sx={{ ...cell, wordBreak: 'break-word', minWidth: { xs: 150, sm: 'auto' } }}>
                                {f.name}
                                {/* The icons can scroll off a phone, so Save sits with the name */}
                                <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 0.5 }}>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        aria-label={`Save ${f.name}`}
                                        onClick={() => actions.download(f)}
                                        disabled={!!gone}
                                        sx={{ minHeight: 44 }}
                                    >
                                        Save
                                    </Button>
                                </Box>
                            </TableCell>
                            <TableCell sx={{ ...cell, color: 'text.secondary', whiteSpace: 'nowrap' }}>{formatSize(f.size)}</TableCell>
                            <TableCell sx={cell}>
                                {who}
                                {rest.length > 0 && (
                                    <Box component="span" sx={{ color: 'text.secondary' }}>
                                        {` · ${rest.join(' · ')}`}
                                    </Box>
                                )}
                            </TableCell>
                            <TableCell align="right" sx={{ ...cell, py: 0.25, whiteSpace: 'nowrap' }}>
                                {f.playable && (
                                    <Tooltip title="Play" disableInteractive>
                                        <IconButton aria-label={`Play ${f.name}`} onClick={() => actions.play(f)} disabled={!!gone}>
                                            <PlayArrowIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Download" disableInteractive>
                                    <IconButton aria-label={`Download ${f.name}`} onClick={() => actions.download(f)} disabled={!!gone}>
                                        <DownloadIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {canDelete && (
                                    <Tooltip title="Delete" disableInteractive>
                                        <IconButton aria-label={`Delete ${f.name}`} onClick={() => actions.remove(f)} disabled={!!gone}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </TableCell>
                        </TableRow>
                    );
                })}
            </TableBody>
        </Table>
    </Box>
);

const LockLine = ({ message, admin }) => (
    <Box
        sx={{
            display: 'flex',
            gap: 1.25,
            alignItems: 'center',
            bgcolor: 'action.hover',
            borderRadius: 1,
            px: 1.5,
            py: 1,
            mx: 2,
            mb: 1,
            fontSize: 14,
            color: 'text.secondary',
        }}
    >
        <LockIcon sx={{ fontSize: 16 }} aria-hidden />
        <span>
            {message} {admin ? "You're an admin, so you can still change it." : 'Only admins can change it.'}
        </span>
    </Box>
);

const UploadButton = ({ contained, onFiles, label = 'Upload' }) => {
    const input = useRef(null);
    return (
        <>
            <Button variant={contained ? 'contained' : 'outlined'} startIcon={<FileUploadOutlinedIcon />} onClick={() => input.current?.click()}>
                {label}
            </Button>
            <input
                ref={input}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = '';
                    if (files.length) onFiles(files);
                }}
            />
        </>
    );
};

/** One folder: header with its own Upload, the lock line, the files, and its drop area. */
const FolderCard = ({ folder, subtitle, yours, chip, admin, uploads, deleting, actions }) => {
    const [dragging, dropHandlers] = useDrop(folder.canChange, (files) => actions.add(folder, files));
    const empty = folder.files.length === 0 && uploads.length === 0;
    const dropText = `Drop to add ${dragging === 1 ? '1 file' : `${dragging} files`} to ${folder.label}`;
    return (
        <Paper
            component="section"
            variant="outlined"
            aria-label={folder.label}
            {...dropHandlers}
            sx={{
                borderRadius: 2,
                position: 'relative',
                ...(yours ? { border: 2, borderColor: 'primary.main' } : {}),
                ...(dragging ? { border: '2px dashed', borderColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) } : {}),
            }}
        >
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', px: 2, pt: 2, pb: 1, flexWrap: 'wrap' }}>
                <FolderIcon sx={{ color: yours ? 'primary.main' : '#90a4ae' }} aria-hidden />
                <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography component="div" sx={{ fontSize: 16, fontWeight: 500 }}>
                        {folder.label}
                        {yours && chip && (
                            <Chip
                                label="Your task"
                                size="small"
                                sx={{ ml: 0.75, height: 20, fontSize: 12, bgcolor: (t) => alpha(t.palette.primary.main, 0.12), color: 'primary.dark' }}
                            />
                        )}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {subtitle}
                    </Typography>
                </Box>
                {folder.canChange && <UploadButton contained={yours} onFiles={(files) => actions.add(folder, files)} />}
            </Box>
            {folder.lockMessage && <LockLine message={folder.lockMessage} admin={admin} />}
            {!empty && (
                <FileTable
                    files={folder.files}
                    uploads={uploads}
                    canDelete={folder.canDelete}
                    deleting={deleting}
                    actions={actions}
                    dim={!!dragging}
                />
            )}
            {folder.canChange && (
                <Box sx={{ px: 2, pt: 1, pb: 2 }}>
                    {dragging ? (
                        <Box
                            sx={{
                                border: 2,
                                borderColor: 'primary.main',
                                borderRadius: 1.5,
                                p: 2,
                                textAlign: 'center',
                                fontSize: 15,
                                fontWeight: 500,
                                color: 'primary.dark',
                                bgcolor: (t) => alpha(t.palette.primary.main, 0.1),
                            }}
                        >
                            {dropText}
                        </Box>
                    ) : empty ? (
                        <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 3.5, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: 15 }}>No files yet</Typography>
                            <Typography variant="caption" color="text.secondary">
                                Drop files here, or use Upload. Pick several at once if you like.
                            </Typography>
                        </Box>
                    ) : (
                        <Box
                            sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1.5, p: 1.25, textAlign: 'center', color: 'text.secondary', fontSize: 13 }}
                        >
                            or drop files here
                        </Box>
                    )}
                </Box>
            )}
            {!folder.canChange && empty && (
                <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
                    No files yet
                </Typography>
            )}
        </Paper>
    );
};

/** A language as one row; clicking it shows its files in place. */
const LanguageRow = ({ folder, open, onToggle, admin, uploads, deleting, actions, isPost }) => {
    const [dragging, dropHandlers] = useDrop(folder.canChange && open, (files) => actions.add(folder, files));
    const status = languageStatus(folder);
    return (
        <Box {...dropHandlers} id={`language-${folder.languageKey}`}>
            <ButtonBase
                aria-expanded={open}
                onClick={onToggle}
                sx={{
                    display: 'flex',
                    width: '100%',
                    gap: 1.5,
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    textAlign: 'left',
                    px: 2,
                    py: 1.5,
                    borderTop: 1,
                    borderColor: 'divider',
                    fontSize: 16,
                }}
            >
                {open ? <ExpandMoreIcon sx={{ color: 'action.active' }} aria-hidden /> : <ChevronRightIcon sx={{ color: 'action.active' }} aria-hidden />}
                <FolderIcon sx={{ fontSize: 20, color: '#90a4ae' }} aria-hidden />
                <Box component="span" sx={{ flex: 1 }}>
                    {folder.label}
                </Box>
                <Typography variant="body2" color="text.secondary" component="span" sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}>
                    {folder.lockMessage && <LockIcon sx={{ fontSize: 14 }} aria-hidden />}
                    {status}
                </Typography>
            </ButtonBase>
            {open && (
                <Box sx={{ pl: 5.5, pr: 0.5, pb: 1, ...(dragging ? { outline: '2px dashed', outlineColor: 'primary.main', bgcolor: (t) => alpha(t.palette.primary.main, 0.04) } : {}) }}>
                    {isPost && folder.caption && (
                        <Box sx={{ px: 1.5, py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Caption
                                </Typography>
                                <CopyButton text={folder.caption} label="Copy caption" ariaLabel={`Copy ${folder.label} caption`} />
                            </Box>
                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {folder.caption}
                            </Typography>
                        </Box>
                    )}
                    {folder.lockMessage && (
                        <Box sx={{ ml: -2 }}>
                            <LockLine message={folder.lockMessage} admin={admin} />
                        </Box>
                    )}
                    {folder.files.length + uploads.length > 0 ? (
                        <FileTable files={folder.files} uploads={uploads} canDelete={folder.canDelete} deleting={deleting} actions={actions} dim={!!dragging} />
                    ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                            No files yet
                        </Typography>
                    )}
                    {folder.canChange && (
                        <Box sx={{ px: 1.5, pt: 1 }}>
                            <UploadButton onFiles={(files) => actions.add(folder, files)} label={`Upload to ${folder.label}`} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

/**
 * Board 3: same name. Cancel / Keep both / Replace. When someone took the name during the upload,
 * the whole file is already in storage, so only the Cancel button throws it away, not Esc or a
 * click beside the dialog.
 */
const NameTakenDialog = ({ item, onChoose }) => {
    const c = item?.conflict;
    return (
        <Dialog open={!!c} onClose={() => !c?.again && onChoose('cancel')} maxWidth="xs" fullWidth>
            {c && (
                <>
                    <DialogTitle>
                        {c.canReplace === false
                            ? `${c.name} is already there`
                            : c.again
                              ? `Someone just uploaded ${c.name}. Replace it?`
                              : `Replace ${c.name}?`}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {c.folder} already has a file with this name. {shortName(c.existingBy)} added it {dayAgo(c.existingAt)}.
                            {c.canReplace === false && ' Keep both adds yours with a number after the name.'}
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => onChoose('cancel')}>Cancel</Button>
                        <Button onClick={() => onChoose('keepBoth')}>Keep both</Button>
                        {c.canReplace !== false && <Button onClick={() => onChoose('replace')}>Replace</Button>}
                    </DialogActions>
                </>
            )}
        </Dialog>
    );
};

/**
 * A small player on the page. The play link lasts 6 hours; if it still runs out (the player asks
 * storage again on every seek), it gets a fresh one and carries on from the same place.
 */
const PlayerDialog = ({ file, service, onClose, onDownload }) => {
    useTrackUsage('PlayerDialog', 'dialog', 'working-folder');
    const [url, setUrl] = useState(null);
    const [error, setError] = useState(null);
    const media = useRef(null);
    const resumeAt = useRef(null);
    const lastRefresh = useRef(0);

    const load = useCallback(async () => {
        try {
            const link = await service.playLink(file.id);
            setUrl(absoluteUrl(link.url));
            setError(null);
        } catch (e) {
            setError(workErrorMessage(e));
        }
    }, [file, service]);

    useEffect(() => {
        load();
    }, [load]);

    const refresh = () => {
        if (Date.now() - lastRefresh.current < 30000) {
            setError("The file can't be played right now. Try Download.");
            return;
        }
        lastRefresh.current = Date.now();
        resumeAt.current = media.current?.currentTime || 0;
        load();
    };
    const resume = () => {
        if (resumeAt.current && media.current) {
            media.current.currentTime = resumeAt.current;
            resumeAt.current = null;
            media.current.play().catch(() => {});
        }
    };
    const audio = (file.mimeType || '').startsWith('audio/') || /\.(wav|mp3|m4a|aac|ogg|flac)$/i.test(file.name);
    const [who, ...rest] = addedLine(file);
    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
            <Box sx={{ display: 'flex', alignItems: 'center', pl: 2.5, pr: 1, pt: 1.5, pb: 1 }}>
                <Typography sx={{ flex: 1, fontSize: 16, fontWeight: 500, wordBreak: 'break-word' }}>{file.name}</Typography>
                <Button onClick={() => onDownload(file)}>Download</Button>
                <IconButton aria-label="Close" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </Box>
            <Box sx={{ mx: 2.5 }}>
                {error && <Alert severity="error">{error}</Alert>}
                {!url && !error && (
                    <Box sx={{ height: 200, bgcolor: '#111', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <CircularProgress size={32} sx={{ color: '#fff' }} />
                    </Box>
                )}
                {/* crossOrigin before src: the app runs under COEP require-corp, as the editor's player does */}
                {url &&
                    (audio ? (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <audio ref={media} crossOrigin="anonymous" src={url} controls autoPlay style={{ width: '100%' }} onError={refresh} onLoadedMetadata={resume} />
                    ) : (
                        // eslint-disable-next-line jsx-a11y/media-has-caption
                        <video
                            ref={media}
                            crossOrigin="anonymous"
                            src={url}
                            controls
                            autoPlay
                            style={{ width: '100%', maxHeight: '60vh', background: '#111', borderRadius: 4 }}
                            onError={refresh}
                            onLoadedMetadata={resume}
                        />
                    ))}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 2.5, pt: 1, pb: 2 }}>
                {[who, ...rest, formatSize(file.size)].join(' · ')}
            </Typography>
        </Dialog>
    );
};

/** Downloads in a hidden frame, so the page (and any upload) stays put. */
const startDownload = (url) => {
    const frame = document.createElement('iframe');
    frame.style.display = 'none';
    frame.src = url;
    document.body.appendChild(frame);
    setTimeout(() => frame.remove(), 60000);
};

const SourceVideoCard = ({ url, label = 'Source video' }) => (
    <Paper variant="outlined" sx={{ borderRadius: 2, px: 2, py: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
        <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 3h8l6 10-4 7H6l-4-7z" fill="none" stroke="#5f6368" strokeWidth="1.6" />
        </svg>
        <Box>
            <Typography sx={{ fontSize: 14, fontWeight: 500 }}>{label}</Typography>
            <Typography variant="caption" color="text.secondary">
                Raw and high-res, on Google Drive
            </Typography>
        </Box>
        <Button href={url} target="_blank" rel="noopener noreferrer" endIcon={<OpenInNewIcon sx={{ fontSize: '14px !important' }} />}>
            Open
        </Button>
    </Paper>
);

/** Asks once for the name an edit link's uploads go under; remembered in this browser. */
const NameDialog = ({ open, onDone, onCancel }) => {
    const [value, setValue] = useState('');
    const ok = value.trim().length > 0;
    return (
        <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>Your name</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>So others know who added the file. It shows next to every file you add.</DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    size="small"
                    label="Name"
                    value={value}
                    inputProps={{ maxLength: 60 }}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && ok && onDone(value.trim())}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" disabled={!ok} onClick={() => onDone(value.trim())}>
                    Continue
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const readName = () => {
    try {
        return localStorage.getItem(NAME_KEY) || '';
    } catch {
        return '';
    }
};

/**
 * /work/video/:videoId?lang=gu, /work/post/:postId?lang=gu and /work/audio/:audioId?lang=gu: the
 * shared folder of a video, post or dubbing audio task (rev6 6c, D23). On a post every language's row starts open, with its caption. One card per folder,
 * each with its own Upload and drop area. Files go from the browser straight to storage, one at a
 * time per card; the API checks every step and only then shows the file.
 *
 * /f/:token: the same page without login, through a public link. The link decides which folders
 * show and whether files can be added; nothing can be deleted or replaced through it.
 */
const WorkFolderPage = ({ darkMode, toggleDarkMode, themeVariant }) => {
    const { videoId, postId, audioId, token } = useParams();
    useTrackUsage(token ? 'WorkFolderLinkPage' : 'WorkFolderPage', 'screen', 'working-folder');
    const contentId = postId || audioId || videoId;
    const contentType = postId ? 'POST' : audioId ? 'AUDIO' : 'VIDEO';
    const service = useMemo(() => (token ? linkFolderService(token) : WorkFileService), [token]);
    const location = useLocation();
    const lang = new URLSearchParams(location.search).get('lang') || null;
    const [sharing, setSharing] = useState(false);
    const [uploaderName, setUploaderName] = useState(readName);
    // Read by add() and run(), which can run before the next render after the name is given
    const nameRef = useRef(uploaderName);
    const [askName, setAskName] = useState(null);
    const [page, setPage] = useState(null);
    const [error, setError] = useState(null);
    const [uploads, setUploads] = useState([]);
    const uploadsRef = useRef([]);
    const controllers = useRef({});
    const lastPaint = useRef({});
    const [deleting, setDeleting] = useState({});
    const [snack, setSnack] = useState(null);
    const [player, setPlayer] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [announcement, setAnnouncement] = useState('');

    const jumpTo = (languageKey) =>
        document.getElementById(`language-${languageKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    const load = useCallback(async () => {
        try {
            setPage(await service.page(contentType, contentId, lang));
            setError(null);
        } catch (e) {
            const status = e?.response?.status;
            setError(token && (status === 404 || !status) ? GONE : workErrorMessage(e));
        }
    }, [service, token, contentType, contentId, lang]);

    // A post's folder opens at the language it came from
    const loaded = !!page;
    useEffect(() => {
        if (loaded && lang && postId) {
            document.getElementById(`language-${lang}`)?.scrollIntoView({ block: 'start' });
        }
    }, [loaded, lang, postId]);

    // Link pages stay out of search engines and don't pass the link on to sites they open
    useEffect(() => {
        if (!token) return undefined;
        const tags = [
            ['robots', 'noindex, nofollow'],
            ['referrer', 'no-referrer'],
        ].map(([name, content]) => {
            const tag = document.createElement('meta');
            tag.setAttribute('name', name);
            tag.setAttribute('content', content);
            document.head.appendChild(tag);
            return tag;
        });
        return () => tags.forEach((t) => t.remove());
    }, [token]);

    useEffect(() => {
        load();
    }, [load]);

    // ------------------------------------------------------------------ upload state

    const commit = (next) => {
        uploadsRef.current = next;
        setUploads(next);
    };
    const patch = (id, changes) => commit(uploadsRef.current.map((u) => (u.localId === id ? { ...u, ...changes } : u)));
    const get = (id) => uploadsRef.current.find((u) => u.localId === id);
    const drop = (id) => commit(uploadsRef.current.filter((u) => u.localId !== id));
    const announce = (text) => setAnnouncement(text);

    const stop = (id, caption) => {
        const u = get(id);
        if (!u) return;
        patch(id, { state: 'stopped', caption: caption || `Stopped at ${Math.floor((u.sent / u.size) * 100)}%. Check your connection.` });
        announce(`${u.name} stopped uploading`);
    };

    const notAdded = (id, e) => {
        const u = get(id);
        if (!u) return;
        const message = workErrorMessage(e);
        const prefix = `${u.name} was not added. `;
        patch(id, {
            state: 'failed',
            title: `${u.name} was not added`,
            caption: message.startsWith(prefix) ? message.slice(prefix.length) : message,
        });
        announce(`${u.name} was not added`);
    };

    const fail = (id, e) => {
        if (e?.aborted || !get(id)) return;
        const code = workErrorCode(e);
        const u = get(id);
        if (code === 'NAME_TAKEN') {
            const d = e.response.data;
            patch(id, {
                state: 'asking',
                conflict: {
                    name: d.name,
                    folder: d.folder,
                    existingBy: d.existingBy,
                    existingAt: d.existingAt,
                    again: !!d.again,
                    canReplace: d.canReplace,
                },
            });
        } else if (code === 'MISSING_PIECES') {
            stop(id, e.response.data.message);
        } else if (NOT_ADDED.includes(code)) {
            notAdded(id, e);
        } else if (e?.network || e?.status || (e?.request && !e?.response)) {
            stop(id);
        } else if (!u.uploadId) {
            // Refused at the start (too many at once, uploads turned off): nothing was sent
            patch(id, { state: 'failed', title: u.name, caption: workErrorMessage(e) });
            announce(`${u.name} can't be added`);
        } else {
            stop(id, workErrorMessage(e));
        }
    };

    const finish = async (id, mode) => {
        const u = get(id);
        if (!u) return;
        patch(id, { state: 'finishing' });
        await service.finishUpload(u.uploadId, mode);
        patch(id, { state: 'done', sent: u.size });
        announce(`${u.name} finished uploading`);
        load();
    };

    const send = async (id, received) => {
        const u = get(id);
        // Cancelled while it was starting
        if (!u) throw { aborted: true }; // eslint-disable-line no-throw-literal
        const controller = new AbortController();
        controllers.current[id] = controller;
        const already = receivedBytes(received, u.size, u.pieceBytes);
        patch(id, { state: 'sending', sent: already, runStartedAt: Date.now(), runStartBytes: already, caption: null });
        await sendPieces({
            file: u.file,
            pieceBytes: u.pieceBytes,
            url: u.url,
            received,
            signal: controller.signal,
            onProgress: (sent) => {
                // A few repaints a second is enough
                const now = Date.now();
                if (now - (lastPaint.current[id] || 0) > 250 || sent === u.size) {
                    lastPaint.current[id] = now;
                    patch(id, { sent });
                }
            },
            freshLink: async () => (await service.uploadState(u.uploadId)).url,
        });
    };

    const run = async (id) => {
        const u = get(id);
        patch(id, { state: 'starting' });
        try {
            const started = await service.startUpload({
                contentType: page?.contentType || contentType,
                contentId: page?.contentId || contentId,
                languageKey: u.languageKey,
                name: u.file.name,
                size: u.file.size,
                mimeType: u.file.type || null,
                mode: u.mode,
                openedFrom: lang,
                uploaderName: token ? nameRef.current : undefined,
            });
            patch(id, { uploadId: started.uploadId, name: started.name, pieceBytes: started.pieceBytes, url: started.url });
            announce(`Uploading ${started.name} to ${u.folderLabel}`);
            await send(id, []);
            await finish(id);
        } catch (e) {
            fail(id, e);
        }
    };

    // One upload at a time per card; the rest wait
    useEffect(() => {
        const current = uploadsRef.current;
        const busy = new Set(current.filter((u) => ['starting', 'sending', 'finishing', 'asking'].includes(u.state)).map((u) => u.folderKey));
        const startedNow = new Set();
        current.forEach((u) => {
            if (u.state === 'waiting' && !busy.has(u.folderKey) && !startedNow.has(u.folderKey)) {
                startedNow.add(u.folderKey);
                run(u.localId);
            }
        });
    }); // eslint-disable-line react-hooks/exhaustive-deps

    const actions = {
        add: (folder, files) => {
            if (token && !nameRef.current) {
                setAskName({ folder, files });
                return;
            }
            const added = files.map((file) => {
                const why = refusal(file, page?.maxBytes);
                return {
                    localId: nextLocalId++,
                    folderKey: folderKey(folder.languageKey),
                    languageKey: folder.languageKey,
                    folderLabel: folder.label,
                    file,
                    name: file.name,
                    size: file.size,
                    sent: 0,
                    // Refused before anything is sent
                    state: why ? 'failed' : 'waiting',
                    title: why ? file.name : null,
                    caption: why,
                };
            });
            commit([...uploadsRef.current, ...added]);
        },
        cancel: (id) => {
            const u = get(id);
            if (!u) return;
            controllers.current[id]?.abort();
            if (u.uploadId) service.cancelUpload(u.uploadId).catch(() => {});
            drop(id);
            announce(`${u.name} cancelled`);
        },
        dismiss: (id) => drop(id),
        retry: async (id) => {
            const u = get(id);
            if (!u.uploadId) {
                patch(id, { state: 'waiting', caption: null });
                return;
            }
            patch(id, { state: 'starting', caption: null });
            try {
                const st = await service.uploadState(u.uploadId);
                if (st.status === 'COMMITTED') {
                    await finish(id);
                } else if (st.status !== 'OPEN') {
                    patch(id, { state: 'failed', title: `${u.name} was not added`, caption: 'The upload was stopped. Upload the file again.' });
                } else {
                    patch(id, { url: st.url });
                    announce(`Uploading ${u.name} again`);
                    await send(id, st.received);
                    await finish(id);
                }
            } catch (e) {
                fail(id, e);
            }
        },
        play: (file) => setPlayer(file),
        download: async (file) => {
            try {
                startDownload(absoluteUrl((await service.downloadLink(file.id)).url));
            } catch (e) {
                setSnack({ message: workErrorMessage(e), error: true });
            }
        },
        remove: (file) => {
            setDeleting((d) => ({ ...d, [file.id]: true }));
            setSnack({ message: `Deleted ${file.name}`, undo: file.id });
            pendingDeletes
                .schedule(file.id)
                .then((r) => {
                    if (!r.undone) return load();
                    return null;
                })
                .catch((e) => setSnack({ message: workErrorMessage(e), error: true }))
                .finally(() =>
                    setDeleting((d) => {
                        const next = { ...d };
                        delete next[file.id];
                        return next;
                    }),
                );
        },
    };

    const choose = async (choice) => {
        const u = uploadsRef.current.find((x) => x.state === 'asking');
        if (!u) return;
        if (choice === 'cancel') {
            actions.cancel(u.localId);
            return;
        }
        if (u.conflict.again && u.uploadId) {
            // The file is already in storage; only the name decision is left
            patch(u.localId, { conflict: null });
            try {
                await finish(u.localId, choice);
            } catch (e) {
                fail(u.localId, e);
            }
        } else {
            patch(u.localId, { state: 'waiting', mode: choice, conflict: null });
        }
    };

    // ------------------------------------------------------------------ delete with Undo (D29)

    const pendingDeletes = useMemo(
        () =>
            createPendingCompletions({
                send: (id) => WorkFileService.remove(id),
                sendKeepalive: (id) => WorkFileService.removeKeepalive(id),
            }),
        [],
    );

    // ------------------------------------------------------------------ the page around the uploads

    useEffect(() => {
        document.title = tabTitle(page?.title || 'Shared folder', uploads);
    }, [page, uploads]);

    // Once someone looks at the tab again, "Done" has been seen
    useEffect(() => {
        const seen = () => {
            if (document.visibilityState === 'visible' && !uploadsRef.current.some((u) => ACTIVE.includes(u.state))) {
                setTimeout(() => commit(uploadsRef.current.filter((u) => u.state !== 'done')), 4000);
            }
        };
        document.addEventListener('visibilitychange', seen);
        return () => document.removeEventListener('visibilitychange', seen);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        // The browser's own "Leave site?"; it can't be reworded, which is why the banner is there
        const beforeUnload = (e) => {
            if (uploadsRef.current.some((u) => ACTIVE.includes(u.state))) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        // Leaving cancels the uploads and sends any delete still waiting for its Undo
        const pageHide = () => {
            uploadsRef.current
                .filter((u) => u.uploadId && ['sending', 'stopped', 'starting', 'asking'].includes(u.state))
                .forEach((u) => service.cancelUploadKeepalive(u.uploadId));
            pendingDeletes.flushKeepalive();
        };
        // A file dropped outside a card must not open in the tab and leave the page
        const noDrop = (e) => e.preventDefault();
        window.addEventListener('beforeunload', beforeUnload);
        window.addEventListener('pagehide', pageHide);
        window.addEventListener('dragover', noDrop);
        window.addEventListener('drop', noDrop);
        return () => {
            window.removeEventListener('beforeunload', beforeUnload);
            window.removeEventListener('pagehide', pageHide);
            window.removeEventListener('dragover', noDrop);
            window.removeEventListener('drop', noDrop);
        };
    }, [pendingDeletes, service]);

    // Without ?lang, so whoever opens it sees the folder from their own side
    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(`${window.location.origin}${location.pathname}`);
            setSnack({ message: 'Link copied. Each person sees only the languages they have access to.' });
        } catch {
            setSnack({ message: "Couldn't copy the link. Copy it from the address bar instead.", error: true });
        }
    };

    const nameGiven = (name) => {
        try {
            localStorage.setItem(NAME_KEY, name);
        } catch {
            // Asked again next time
        }
        nameRef.current = name;
        setUploaderName(name);
        const pending = askName;
        setAskName(null);
        if (pending?.files) actions.add(pending.folder, pending.files);
    };

    const header = token ? (
        <Box component="header" sx={{ backgroundColor: '#2C3D50', color: '#fff', py: 1.5 }}>
            <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <img src="/SRMD-Logo.png" alt="SRMD" style={{ height: 36 }} />
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                    Shared via AmrutVaani
                </Typography>
            </Box>
        </Box>
    ) : (
        <PageHeader darkMode={darkMode} toggleDarkMode={toggleDarkMode} themeVariant={themeVariant} />
    );
    if (error) {
        return (
            <>
                {header}
                <Box sx={{ maxWidth: 1120, mx: 'auto', p: 3 }}>
                    <Alert severity="error">{error}</Alert>
                </Box>
            </>
        );
    }
    if (!page) {
        return (
            <>
                {header}
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            </>
        );
    }

    const isPost = page.contentType === 'POST';
    const videoFolder = page.folders.find((f) => !f.languageKey);
    // A post's languages stay in one list, so everything is on one page grouped by language
    const yours = page.openedFromLanguage && !isPost ? page.folders.find((f) => f.languageKey === page.openedFromLanguage) : null;
    const others = page.folders.filter((f) => f.languageKey && f !== yours);
    const uploadsFor = (folder) => uploads.filter((u) => u.folderKey === folderKey(folder.languageKey) && u.state !== 'done');
    const banner = bannerText(uploads);
    const asking = uploads.find((u) => u.state === 'asking');
    const card = (folder, subtitle, isYours) => (
        <FolderCard
            folder={folder}
            subtitle={subtitle}
            yours={isYours}
            chip={!token}
            admin={page.admin}
            uploads={uploadsFor(folder)}
            deleting={deleting}
            actions={actions}
        />
    );

    return (
        <>
            {header}
            <Box sx={{ bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' }}>
                <Box sx={{ maxWidth: 1120, mx: 'auto', px: { xs: 2, sm: 3 }, pt: 3, pb: 6, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'flex-start', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                            {!token && (
                                <Button
                                    component={RouterLink}
                                    to="/tasks"
                                    size="small"
                                    startIcon={<ArrowBackIcon />}
                                    sx={{ alignSelf: 'flex-start', ml: -1, mb: 0.5 }}
                                >
                                    My tasks
                                </Button>
                            )}
                            <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'text.secondary' }}>Shared folder</Typography>
                            <Typography component="h1" sx={{ m: 0, fontSize: 28, fontWeight: 400 }}>
                                {page.title}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
                                <Chip label={isPost ? 'Social post' : page.contentType === 'AUDIO' ? 'Dubbing audio' : 'Video'} size="small" />
                                {page.openedFromLabel && !token && (
                                    <Typography variant="body2" color="text.secondary" component="span">
                                        Opened from <b>{[page.openedFromLabel, page.openedFromStage].filter(Boolean).join(' · ')}</b>
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        {page.sourceUrl && <SourceVideoCard url={page.sourceUrl} label={isPost ? 'Source' : undefined} />}
                    </Box>
                    {page.waiting && (
                        <Alert severity="info" sx={{ mt: 1.5 }}>
                            {page.title} is waiting for its video. Upload it here.
                        </Alert>
                    )}

                    {token ? (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                            <LinkIcon sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden />
                            <Typography variant="body2" color="text.secondary">
                                {page.access === 'EDIT'
                                    ? "Shared with you by a link. You can see, play and download these files, and add new ones. You can't delete or replace files."
                                    : 'Shared with you by a link. You can see, play and download these files.'}
                                {page.linkExpiresAt ? ` This link works until ${new Date(page.linkExpiresAt).toLocaleDateString()}.` : ''}
                                {page.access === 'EDIT' && uploaderName && (
                                    <>
                                        {' '}
                                        Files you add show as <b>{uploaderName}</b>.{' '}
                                        <Button size="small" sx={{ p: 0, minWidth: 0, verticalAlign: 'baseline' }} onClick={() => setAskName({})}>
                                            Change
                                        </Button>
                                    </>
                                )}
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                            <LockIcon sx={{ fontSize: 18, color: 'text.secondary' }} aria-hidden />
                            <Typography variant="body2" color="text.secondary">
                                {isPost
                                    ? 'PMs, admins and AV see every language. Others see the languages they work on'
                                    : 'PMs and admins see every language. Others see the languages they work on or have on their profile'}
                                {page.canShare ? ', and anyone you give a public link sees what it reaches.' : '. To share it outside the team, ask a PM for a public link.'}
                            </Typography>
                            <Button variant="outlined" size="small" startIcon={<LinkIcon />} onClick={copyLink}>
                                Copy link
                            </Button>
                            {page.canShare && (
                                <Button variant="contained" size="small" startIcon={<ShareIcon />} onClick={() => setSharing(true)}>
                                    Share
                                </Button>
                            )}
                        </Box>
                    )}

                    {banner && (
                        <Box
                            sx={{
                                position: 'sticky',
                                top: 0,
                                zIndex: 2,
                                bgcolor: (t) => alpha(t.palette.info.main, 0.12),
                                color: 'info.dark',
                                borderRadius: 1,
                                px: 2,
                                py: 1.25,
                                fontSize: 14,
                                display: 'flex',
                                gap: 1.25,
                                alignItems: 'center',
                                backdropFilter: 'blur(4px)',
                            }}
                        >
                            <InfoOutlinedIcon sx={{ fontSize: 20 }} aria-hidden />
                            <span>{banner}</span>
                        </Box>
                    )}
                    <Box role="status" aria-live="polite" sx={visuallyHidden}>
                        {announcement}
                    </Box>

                    {isPost && others.length > 1 && (
                        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }} aria-label="Jump to a language">
                            {others.map((f) => (
                                <Chip key={f.languageKey} size="small" label={f.label} clickable onClick={() => jumpTo(f.languageKey)} />
                            ))}
                        </Box>
                    )}
                    {videoFolder &&
                        card(videoFolder, isPost || page.contentType === 'AUDIO' ? 'Files every language uses' : 'Files every language uses, like the master edit or music', false)}
                    {yours && card(yours, `Dubbed audio, the ${yours.label} edit, anything only ${yours.label} uses`, true)}

                    {others.length > 0 && (
                        <Paper component="section" variant="outlined" aria-label={yours ? 'Other languages' : 'Languages'} sx={{ borderRadius: 2 }}>
                            <Box sx={{ px: 2, pt: 2, pb: 0.5 }}>
                                <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{yours ? 'Other languages' : 'Languages'}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {others.some((f) => f.canChange)
                                        ? `Click a language to ${isPost ? 'fold' : 'show'} its files.`
                                        : `You can see and download these but not change them. Click a language to ${isPost ? 'fold' : 'show'} its files.`}
                                </Typography>
                            </Box>
                            {others.map((f) => (
                                <LanguageRow
                                    key={f.languageKey}
                                    folder={f}
                                    isPost={isPost}
                                    open={isPost ? expanded[f.languageKey] !== false : !!expanded[f.languageKey]}
                                    onToggle={() =>
                                        setExpanded((x) => ({
                                            ...x,
                                            [f.languageKey]: isPost ? x[f.languageKey] === false : !x[f.languageKey],
                                        }))
                                    }
                                    admin={page.admin}
                                    uploads={uploadsFor(f)}
                                    deleting={deleting}
                                    actions={actions}
                                />
                            ))}
                        </Paper>
                    )}

                    <Typography variant="caption" color="text.secondary">
                        Files up to {formatSize(page.maxBytes)}. Programs (.exe, .msi and similar) can't be added. Audio and video play on this
                        page; other files download.
                    </Typography>
                </Box>
            </Box>

            <NameTakenDialog item={asking} onChoose={choose} />
            <NameDialog open={!!askName} onDone={nameGiven} onCancel={() => setAskName(null)} />
            {page.canShare && <WorkShareDialog open={sharing} onClose={() => setSharing(false)} page={page} />}
            {player && <PlayerDialog file={player} service={service} onClose={() => setPlayer(null)} onDownload={actions.download} />}
            <Snackbar
                open={!!snack}
                autoHideDuration={snack?.undo ? UNDO_SECONDS * 1000 : 6000}
                onClose={(_, reason) => reason !== 'clickaway' && setSnack(null)}
                message={snack?.message}
                action={
                    snack?.undo ? (
                        <Button
                            color="secondary"
                            size="small"
                            onClick={() => {
                                pendingDeletes.undo(snack.undo);
                                setSnack(null);
                            }}
                        >
                            Undo
                        </Button>
                    ) : null
                }
                ContentProps={snack?.error ? { sx: { bgcolor: 'error.main' } } : undefined}
            />
        </>
    );
};

export default WorkFolderPage;
